/**
 * The catalog editor's form controls (plan 8.1 "a structured form ... the
 * markdown subset in textareas"; section 13, G13). Thin wrappers over the
 * shared fieldCls (frontend/src/ui/styles, imported by file path) that tie a
 * visible label to its control, so every field is labelled for a screen
 * reader and every control keeps the 44 px mobile target.
 */
import { useId } from 'react';
import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { fieldCls, iconBtnCls, outlineBtnCls } from '../../../../ui/styles';
import { checkboxCls, hintCls, labelCls } from './styles';

interface FieldFrameProps {
  id: string;
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  className?: string;
}

function FieldFrame({ id, label, hint, children, className }: FieldFrameProps) {
  return (
    <div className={`space-y-1 ${className ?? ''}`.trim()}>
      <label htmlFor={id} className={labelCls}>
        {label}
      </label>
      {children}
      {hint ? <div className={hintCls}>{hint}</div> : null}
    </div>
  );
}

export interface TextInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId?: string;
  placeholder?: string;
  hint?: ReactNode;
  type?: 'text' | 'url' | 'date';
  className?: string;
  disabled?: boolean;
}

export function TextInput({ label, value, onChange, testId, placeholder, hint, type = 'text', className, disabled }: TextInputProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} className={className}>
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`${fieldCls} min-h-[44px] w-full disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0`}
      />
    </FieldFrame>
  );
}

export interface TextAreaProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  testId?: string;
  rows?: number;
  hint?: ReactNode;
  placeholder?: string;
  className?: string;
  /** Markdown fields read better in a monospace face (the Appendix E subset). */
  markdown?: boolean;
}

export function TextArea({ label, value, onChange, testId, rows = 3, hint, placeholder, className, markdown }: TextAreaProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} className={className}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`${fieldCls} w-full ${markdown ? 'font-mono text-xs leading-relaxed' : ''}`}
      />
    </FieldFrame>
  );
}

export interface NumberInputProps {
  label: string;
  value: number | null;
  /** null when the field is emptied (allowed only with allowEmpty). */
  onChange: (value: number | null) => void;
  testId?: string;
  min?: number;
  max?: number;
  hint?: ReactNode;
  allowEmpty?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * A whole-number field. It keeps no text of its own: an entry that is not a
 * whole number in range is not passed on (the server would refuse it, and the
 * autosave never sends a value a person did not mean).
 */
export function NumberInput({ label, value, onChange, testId, min, max, hint, allowEmpty, placeholder, className }: NumberInputProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} className={className}>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        step={1}
        value={value === null || value === undefined ? '' : String(value)}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === '') {
            if (allowEmpty) onChange(null);
            return;
          }
          const n = Number(raw);
          if (!Number.isInteger(n)) return;
          if (min !== undefined && n < min) return;
          if (max !== undefined && n > max) return;
          onChange(n);
        }}
        data-testid={testId}
        className={`${fieldCls} min-h-[44px] w-full tabular-nums sm:min-h-0`}
      />
    </FieldFrame>
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  testId?: string;
  hint?: ReactNode;
  className?: string;
  disabled?: boolean;
}

export function SelectField({ label, value, onChange, options, testId, hint, className, disabled }: SelectFieldProps) {
  const id = useId();
  return (
    <FieldFrame id={id} label={label} hint={hint} className={className}>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={`${fieldCls} min-h-[44px] w-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldFrame>
  );
}

export interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  testId?: string;
  hint?: ReactNode;
}

export function Checkbox({ label, checked, onChange, testId, hint }: CheckboxProps) {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 text-sm text-gray-800 dark:text-gray-200 sm:min-h-0">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
          className={checkboxCls}
        />
        {label}
      </label>
      {hint ? <div className={hintCls}>{hint}</div> : null}
    </div>
  );
}

/** Move up, move down and remove, for one row of an ordered list. */
export function RowActions({
  index,
  count,
  onMove,
  onRemove,
  label,
  canRemove = true,
}: {
  index: number;
  count: number;
  onMove: (to: number) => void;
  onRemove: () => void;
  /** What the row is, for the buttons' labels ("slide 3"). */
  label: string;
  canRemove?: boolean;
}) {
  return (
    <div className="flex flex-shrink-0 items-center gap-1">
      <button type="button" className={iconBtnCls} onClick={() => onMove(index - 1)} disabled={index === 0} aria-label={`Move ${label} up`}>
        <ArrowUp className="h-4 w-4" aria-hidden="true" />
      </button>
      <button
        type="button"
        className={iconBtnCls}
        onClick={() => onMove(index + 1)}
        disabled={index >= count - 1}
        aria-label={`Move ${label} down`}
      >
        <ArrowDown className="h-4 w-4" aria-hidden="true" />
      </button>
      <button type="button" className={iconBtnCls} onClick={onRemove} disabled={!canRemove} aria-label={`Remove ${label}`}>
        <Trash2 className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

export interface StringListProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  /** The most items the lint accepts (the add button stops there). */
  max?: number;
  addLabel: string;
  testId?: string;
  hint?: ReactNode;
  /** One word for an item, for the row buttons' labels. */
  itemName: string;
}

/**
 * An ordered list of strings (objectives, takeaways, "what to remember",
 * bullets): one input per item, add, move and remove.
 */
export function StringList({ label, items, onChange, max, addLabel, testId, hint, itemName }: StringListProps) {
  const baseId = useId();
  const list = Array.isArray(items) ? items : [];
  const atMax = max !== undefined && list.length >= max;
  return (
    <fieldset className="space-y-2" data-testid={testId}>
      <legend className={labelCls}>{label}</legend>
      {list.map((item, i) => (
        <div key={i} className="flex items-start gap-2">
          <input
            id={`${baseId}-${i}`}
            aria-label={`${itemName} ${i + 1}`}
            value={item}
            onChange={(e) => onChange(list.map((v, j) => (j === i ? e.target.value : v)))}
            data-testid={testId ? `${testId}-${i}` : undefined}
            className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
          />
          <RowActions
            index={i}
            count={list.length}
            label={`${itemName} ${i + 1}`}
            onMove={(to) => {
              if (to < 0 || to >= list.length) return;
              const next = [...list];
              const [moved] = next.splice(i, 1);
              next.splice(to, 0, moved);
              onChange(next);
            }}
            onRemove={() => onChange(list.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className={`${outlineBtnCls()} disabled:cursor-not-allowed disabled:opacity-50`}
          onClick={() => onChange([...list, ''])}
          disabled={atMax}
          data-testid={testId ? `${testId}-add` : undefined}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {addLabel}
        </button>
        {hint ? <span className={hintCls}>{hint}</span> : null}
      </div>
    </fieldset>
  );
}
